import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import type { PaginatedUsersResponse } from "./follows";

/**
 * `GET /api/users/?q=&is_creator=&limit=&offset=` (仕様書 §6.6) のクエリ。
 *
 * writer 権限（`MASTERADMIN` / `CURATOR`）専用エンドポイントで、ロール割当 UI 等で
 * CREATOR ユーザーを横断検索する用途。検索対象は常に `AuthUser.role==CREATOR` で、
 * `is_creator` パラメータは互換のため受理されるが現状は事実上 true 固定。
 */
export interface SearchUsersParams {
  q?: string;
  is_creator?: boolean;
  limit?: number;
  offset?: number;
}

const buildSearchUsersQueryString = ({
  q,
  is_creator,
  limit,
  offset,
}: SearchUsersParams): string => {
  const params: Record<string, string> = {};
  if (q !== undefined && q !== "") params.q = q;
  if (is_creator) params.is_creator = "1";
  if (limit !== undefined) params.limit = String(limit);
  if (offset !== undefined) params.offset = String(offset);
  const qs = new URLSearchParams(params).toString();
  return qs ? `?${qs}` : "";
};

export const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["UserSearch"],
  endpoints: (builder) => ({
    searchUsers: builder.query<PaginatedUsersResponse, SearchUsersParams>({
      query: (params) => `/api/users/${buildSearchUsersQueryString(params)}`,
      providesTags: (_result, _error, { q, is_creator, limit, offset }) => [
        {
          type: "UserSearch",
          id: `${q ?? ""}:${is_creator ? 1 : 0}:${limit ?? ""}:${offset ?? ""}`,
        },
      ],
    }),
  }),
});

export const { useSearchUsersQuery, useLazySearchUsersQuery } = usersApi;
